import { Divider } from '@fs/zion-ui'
import type { CategoryType } from '@fs/zion-tree-types'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import useMyHeritageHintsFlag from '../../data-service/useMyHeritageHintsFlag'
import useQualityIndicatorFlag from '../../data-service/useQualityIndicatorFlag'
import { getCategoryTitleMap } from '../getCategoryTitleMap'
import CategorySkeleton from './CategorySkeleton'
import MyHeritageMatchesSectionSkeleton from './MyHeritageMatchesSectionSkeleton'

type Props = {
  /** Set by the research help surfaces, the ones with a Third Party Hints section. The lab flag decides who sees it. */
  showMyHeritageHints?: boolean
  /** Set by the dismissed hints page, the only surface with a Quality Score Improvements section. */
  showQualityScoreImprovements?: boolean
}

// Skeleton for the research help surfaces and the dismissed hints page. It renders the sections
// whose presence is known without a data call; "Matches From Other Trees" needs a request, so it
// stays out.
//
// The optional sections are opt-in because a section promised here that never arrives collapses
// when the data lands, shifting everything below it up. Callers pass their intent and this pairs it
// with the governing flag, which is also what lets Third Party Hints be reserved before the focus
// person exists — useShowMyHeritageHints cannot answer yet, and a skeleton makes no request.
export default function ResearchHelpContentSkeleton({
  showMyHeritageHints = false,
  showQualityScoreImprovements = false,
}: Props = {}): JSX.Element {
  const [t] = useTranslation()
  const { data: myHeritageFlag } = useMyHeritageHintsFlag()
  const { data: qualityIndicatorFlag } = useQualityIndicatorFlag()
  const titleMap = getCategoryTitleMap(t)

  const categories: CategoryType[] = ['data-problems', 'record-hints', 'research-suggestions']
  if (showQualityScoreImprovements && qualityIndicatorFlag?.isOn) {
    categories.push('quality-score-improvements')
  }

  return (
    <>
      {categories.map((category, index) => (
        <Fragment key={category}>
          {index > 0 && <Divider marginY="xxs" />}
          <CategorySkeleton title={titleMap[category]} />
        </Fragment>
      ))}
      {showMyHeritageHints && myHeritageFlag?.isOn && (
        <>
          <Divider marginY="xxs" />
          <MyHeritageMatchesSectionSkeleton />
        </>
      )}
    </>
  )
}
